import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';
import logger from '../services/logger';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };

    // Global error handlers for uncaught errors
    if (typeof window !== 'undefined') {
      window.onerror = (message, source, lineno, colno, error) => {
        this.reportError(String(message), error?.stack || `${source}:${lineno}:${colno}`);
      };
      window.onunhandledrejection = (event) => {
        this.reportError('Unhandled Promise: ' + String(event.reason), (event.reason as Error)?.stack);
      };
    }
  }

  private reportError(message: string, stack?: string) {
    try {
      fetch('/api/log-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        }),
      }).catch(() => {}); // Fire and forget — network may be down
    } catch (reportErr) {
      logger.error('ErrorBoundary', 'Error reporting itself failed:', reportErr);
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary', 'Fahryo Error:', error, { componentStack: errorInfo.componentStack });
    this.reportError(error.message, error.stack);
    // Send to Sentry with React component stack
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    // Clear local storage and reload
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: 20,
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: 48,
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>😵</div>
            <h1 style={{
              color: '#fff',
              fontSize: 24,
              fontWeight: 700,
              margin: '0 0 12px',
            }}>
              {i18n.language === 'de' ? 'Ups! Etwas ist schiefgelaufen' : 'Oops! Something went wrong'}
            </h1>
            <p style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: 15,
              lineHeight: 1.6,
              margin: '0 0 32px',
            }}>
              {i18n.language === 'de'
                ? 'Keine Sorge — dein Lernfortschritt ist gespeichert. Versuche die Seite neu zu laden.'
                : 'Don\'t worry — your learning progress is saved. Try reloading the page.'}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={this.handleReload}
                style={{
                  background: 'var(--f-brand, #E03705)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 28px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onMouseOver={e => (e.target as HTMLElement).style.transform = 'scale(1.02)'}
                onMouseOut={e => (e.target as HTMLElement).style.transform = 'scale(1)'}
              >
                🔄 {i18n.language === 'de' ? 'Seite neu laden' : 'Reload page'}
              </button>

              <button
                onClick={this.handleReset}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  padding: '12px 28px',
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseOver={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.15)'}
                onMouseOut={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.1)'}
              >
                {i18n.language === 'de' ? 'Cache leeren & neu starten' : 'Clear cache & restart'}
              </button>
            </div>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <details style={{
                marginTop: 24,
                textAlign: 'left',
                background: 'rgba(255,0,0,0.1)',
                borderRadius: 8,
                padding: 12,
              }}>
                <summary style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 12 }}>
                  {i18n.language === 'de' ? 'Technische Details' : 'Technical Details'}
                </summary>
                <pre style={{
                  color: '#ff6b6b',
                  fontSize: 11,
                  overflow: 'auto',
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}>
                  {this.state.error.message}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
